define('edap',['basil', 'doneable'], function(Basil, Doneable) {
    'use strict';

    var edapOpts = window.edapOptions || {};

    // Set a default for the EDAP window namespace
    if (!edapOpts.ns) {
        edapOpts.ns = 'edap';
    }

    // Set a default for the ADL window property name
    if (!edapOpts.dataLayerName) {
        edapOpts.dataLayerName = 'analyticsdatalayer';
    }

    /**
     *  Event-driven Data Abstraction Platform (EDAP)
     *
     *  EDAP is a front-end event bus that is purpose built to handle event-based analytics. Some of the primary
     *  advantages are:
     *   - Increases data integrity since all callbacks are based on the same event, with the same data
     *   - Allows for extending your data layer on a per-event basis without worrying about race conditions
     *   - Has a built-in history function to help understand what is happening on the event bus
     *   - Can handle page-transition events on the next page, therefore increasing the chances of data being collected
     *   - Event listeners have the ability to report when async operations are complete
     *
     *  @constructor
     *  @param {Object} [edapOptions] - Supported properties:
     *                                      {String} [dataLayerName] - name of the data layer in the window (default: "analyticsdatalayer")
     *                                      {String} [ns] - window namespace to expose EDAP (default: "edap")
     *                                      {Boolean} [noAMD] - when set to true it will not expose EDAP as a requirejs module
     */
    function Edap(edapOptions) {
        var persistentLogger = new Basil({
                namespace: 'edap',
                storages: ['local', 'cookie'],
                expireDays: 14
            }),
            loggingVersion = '1.0.0',
            loggingKey = 'delayProcessing',
            defaultCmdsFormat = {ver: loggingVersion, cmds: []},
            uuidRegex = new RegExp('^[a-z0-9]{8}-[a-z0-9]{4}-4[a-z0-9]{3}-[a-z0-9]{4}-[a-z0-9]{12}$'),
            ignoredConsoleMessages = [
                'edap.listener.added',
                'edap.listener.removed',
                'edap.state'
            ],
            edapThis,
            edapInternal;

        this.version = '0.2.0';
        this.globalOptions = edapOptions;

        edapThis = this;
        this.internal = {};
        edapInternal = this.internal;

        /**
         * 
         * Internal Methods
         *
         */
         
        edapInternal.reset = function() {
            edapThis.logs = {
                log: [],
                error: []
            };

            edapInternal.callbacks = {};
            edapInternal.history = [];
            edapInternal.scopes = {};
            edapInternal.savedCmds = null;
            edapInternal.config = {
                maxWaitTime: 2  // in seconds
            };

            edapInternal.setState('initting');
        };


        /**
         *  Abstraction around getting the window-level EDAP singleton
         *
         *  @private
         *  @returns {Object} - Returns the window EDAP singleton
         */
        edapInternal.getGlobal = function() {
            return window[edapThis.globalOptions.ns];
        };



        /**
         *  Logging feature for debugging level messaging.
         *  All log entries will be available in Edap.logs.log
         *
         *  @private
         *  @param {Any} - All arguments are wrapped in an Array and pushed exactly as they were called
         */
        edapInternal.log = function() {
            edapThis.logs.log.push(arguments);
        };


        /**
         *  Error logger.
         *  All log entries will be available in Edap.logs.error
         *
         *  @private
         *  @param {Error|String} [err] - If a String is passed then it will be used to instantiate a new Error.
         *                                If an instance of Error is passed then it will be pushed as is.
         */
        edapInternal.error = function(err) {
            var newErr;

            if (err instanceof Error) {
                newErr = err;
            } else {
                try {
                    newErr = new Error(err);
                } catch(e) {
                    newErr = err;
                }
            }

            edapThis.logs.error.push(newErr);
        };



        /**
         *  History logger.
         *  All log entries will be available in Edap.internal.history
         *
         *  @private
         *  @param {String} [log] - Wraps the String in an Array and pushes into the log
         */
        edapInternal.logHistory = function(log) {
            edapInternal.history.push([log]);
        };



        /**
         *  Should be used only once when flushing any existing window.edap queued commands.
         *  It will run each command by passing it into Edap.push().
         *  @see edapThis.push
         *
         *  @private
         *  @param {Array} [cmds] - Array of Arrays of commands to execute
         */
        edapInternal.flushAsyncCommands = function(cmds) {
            var i,
                len;

            for (i=0, len=cmds.length; i<len; i++) {
                edapThis.push.call(edapThis,cmds[i]);
            }
        };



        /**
         *  Utility function to check to see if an item already eixsts in the list.
         *  Equality is based on type and value (===).
         *
         *  @private
         *  @param {Object} [item] - The item being searched for in the list
         *  @param {Array} [list] - The list to be searched
         *  @returns {Boolean} - If the item is found in the list
         */
        edapInternal.alreadyExists = function(item, list) {
            var i,
                len;

            for(i=0, len=list.length; i<len; i++) {
                if (item === list[i]) {
                    return true;
                }
            }

            return false;
        };



        /**
         *  Recursive clone function that handles Arrays, Objects and pass-by-value types. The primary
         *  purpose is to clone relatively simple ADL-like data structures. It is not recommened for
         *  highly complex data structures that have circular references.
         *
         *  NOTE: This implementation uses TypeError's in place of any circular references.
         *
         *  @private
         *  @param {Object} [input] - Item to be cloned
         *  @param {Array} [seen] - List of objects that have been seen to prevent circular references
         *  @returns {Boolean} - If the item is found in the list
         */
        edapInternal.clone = function(input, seen) {
            var tInput = typeof input,
                i,
                len,
                ret;

            seen = seen || [];

            // Null needs to be handled as a special case since it is an undefined Object
            if (input === null) {

                ret = null;

            // recursively clone all the elements of this array
            } else if (input instanceof Array) {
                ret = [];

                if (edapInternal.alreadyExists(input,seen)) {
                    return new TypeError('Circular Array reference found');
                }

                seen.push(input);
                for(i=0, len=input.length; i<len; i++) {
                    ret[i] = edapInternal.clone(input[i], seen);
                }
                seen.pop();

            // recursively clone all the properties of this object
            } else if (tInput === 'object') {
                ret = {};

                if (edapInternal.alreadyExists(input,seen)) {
                    return new TypeError('Circular Object reference found');
                }

                seen.push(input);
                for(i in input) {
                    if (input.hasOwnProperty(i)) {
                        ret[i] = edapInternal.clone(input[i], seen);
                    }
                }
                seen.pop();

            // If it isn't an object or an array then assume that it pass-by-value
            } else {
                ret = input;
            }

            return ret;
        };


        /**
         *  Extend functon that will give you back a cloned src object that has other's values overwritten
         *  in it.
         *
         *  @private
         *  @param {Object} [src] - Base object
         *  @param {Object} [other] - Extend object that will overwrite any existing values in the src object
         *  @returns {Object} - Cloned src object with other data extended into it
         */
        edapInternal.extend = function(src, other) {
            var retObj = {},
                i;

            if (src) {
                retObj = edapInternal.clone(src);
            }

            for(i in other) {
                if (other.hasOwnProperty(i)) {

                    // '-1', null and undefined are all considered "does not exist"
                    if (!retObj.hasOwnProperty(i) || 
                        (retObj.hasOwnProperty(i) && other[i] !== '-1' && other[i] !== null && other[i] !== undefined)) {

                        retObj[i] = edapInternal.clone(other[i]);
                    }
                }
            }

            return retObj;
        };



        /**
         *  Extend functon that will give you back a cloned src object that has other's values overwritten
         *  in it.
         *
         *  @private
         *  @param {String} [state] - New state of EDAP
         *  @fires edap.state
         *  @returns {Object} - Cloned src object with other data extended into it
         */
        edapInternal.setState = function(state) {
            edapInternal.state = state;
            edapThis.trigger('edap.state', {edapstate: state});
        };



        /**
         *  Stores a command into persistent storage. Currently the order of preference on client-side
         *  storage is:
         *    - localStorage
         *    - cookie
         *
         *  @private
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event
         *  @param {String} [data] - Event-specific data to be extended into the global Data Layer
         *  @returns {Boolean} - true: if stored, false: if not
         */
        edapInternal.storeCmd = function(eventName, data) {
            var existingCmds,
                err;

            if (typeof eventName !== 'string') {
                err = new TypeError('storeCmd(): eventName must be a string');
                edapInternal.error(err);
                throw err;
            }

            try {
                existingCmds = persistentLogger.get(loggingKey);

                if (existingCmds === null) {
                    existingCmds = edapInternal.clone(defaultCmdsFormat);
                }
            } catch(e) {
                edapInternal.error(e);
                existingCmds = edapInternal.clone(defaultCmdsFormat);
            }

            if (existingCmds && existingCmds.hasOwnProperty('ver')) {
                existingCmds.cmds.push([eventName, data]);
                persistentLogger.set(loggingKey, existingCmds);
                return true;
            }

            return false;
        };


        /**
         *  Removes an item from client-side storage based on matching the eventName and JSON.stringify
         *  matching the data.
         *
         *  @private
         *  @param {String} [eventName] - Name of the event
         *  @param {String} [data] - Event-specific data to be extended into the global Data Layer
         *  @returns {Boolean} - true: if successful, false: if not
         */
        edapInternal.removeCmd = function(eventName, data) {
            var existingCmds,
                jsonData,
                i,
                len;

            try {
                existingCmds = persistentLogger.get(loggingKey);
                jsonData = JSON.stringify(data);

                if (existingCmds === null) {
                    return true;
                }

                if (existingCmds && existingCmds.hasOwnProperty('ver')) {
                    for(i=0, len=existingCmds.cmds.length; i<len; i++) {
                        if (eventName === existingCmds.cmds[i][0] && jsonData === JSON.stringify(existingCmds.cmds[i][1])) {
                            existingCmds.cmds.splice(i, 1);

                            // Force this loop to end
                            i = len;
                        }
                    }
                    persistentLogger.set(loggingKey, existingCmds);
                    return true;
                }
            } catch(e) {
                edapInternal.error(e);
            }

            return false;
        };


        /**
         *  Execute all the client-side stored events
         *
         *  @private
         *  @param {String} [eventName] - Name of the event
         *  @param {String} [data] - Event-specific data to be extended into the global Data Layer
         *  @returns {Doneable} - Will resolve when all the stored events have completed processing
         */
        edapInternal.processStoredCmds = function() {
            return new Doneable(function(done) {
                var completeCmds = 0,
                    existingCmds,
                    err,
                    numCmds,
                    i;

                function checkDone() {
                    completeCmds++;
                    if (completeCmds === numCmds) {
                        done();
                    }
                }

                function triggerCmd(idx) {
                    var eventName = existingCmds.cmds[idx][0],
                        data = existingCmds.cmds[idx][1],
                        newData = edapInternal.extend(edapThis.getDL(), data);

                    edapThis.trigger(eventName, newData).then(function() {
                        edapInternal.removeCmd(eventName, data);
                        checkDone();
                    });
                }


                try {
                    existingCmds = persistentLogger.get(loggingKey);
                } catch(e) {
                    edapInternal.error(e);
                    done();
                    return;
                }

                if (existingCmds === null) {
                    done();
                    return;
                }

                if (!existingCmds || !existingCmds.hasOwnProperty('ver')) {
                    err = new TypeError('processStoredCmds(): stored values not in the correct format: '+
                        (JSON && JSON.stringify ? JSON.stringify(existingCmds) : ''));
                    edapInternal.error(err);
                    done();
                    return;
                }

                numCmds = existingCmds.cmds.length;

                for(i=0; i<numCmds; i++) {
                    triggerCmd(i);
                }
            }, {error: edapThis.error});
        };



        /**
         *  Gets a list of related callbacks to an event name. Events can be hierarchical so the the
         *  hierarchy is traversed in most specific to least specific order. Example:
         *    eventName: 'test.foo.bar'
         *    Callbacks would be concatentated in the following order:
         *      test.foo.bar
         *      test.foo.*
         *      test.foo
         *      test.*
         *      test
         *      *
         *
         *  @private
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event
         *  @returns {Array} - Array of function callbacks related to eventName in
         *                     most specific to least specific order
         */
        edapInternal.getCallbacks = function(eventName) {
            var logName = 'getCallbacks',
                ret = [],
                parts,
                newParts,
                newEventName,
                err,
                i;

            if (typeof eventName !== 'string') {
                err = new TypeError(logName + '(): eventName must be a string');
                edapInternal.error(err);
                throw err;
            }

            parts = eventName.split('.');
            for (i=parts.length; i>0; i--) {
                newParts = parts.slice(0,i);
                newEventName = newParts.join('.');

                if (eventName === newEventName) {
                    if (edapInternal.callbacks.hasOwnProperty(newEventName)) {
                        ret = ret.concat(edapInternal.callbacks[newEventName]);
                    }
                }

                newParts[i-1] = '*';
                newEventName = newParts.join('.');

                if (edapInternal.callbacks.hasOwnProperty(newEventName)) {
                    ret = ret.concat(edapInternal.callbacks[newEventName]);
                }
            }
 
            return ret;
        };


        /**
         *  Create a UUID
         *  Borrowed from core-ui-utilities repo
         *  http://byronsalau.com/blog/how-to-create-a-guid-uuid-in-javascript/
         *
         *  @returns {String} - rfc4122 version 4 compliant guid
         */
        edapInternal.createUuid = function() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                /*jshint bitwise: false*/
                var r = Math.random() * 16 | 0,
                    v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        };


        /**
         *  Getter for window.console
         *
         *  @returns {Object|null} - Either returns window.console or null
         */
        edapInternal.getConsole = function() {
            if (window.console === undefined || window.console === null) {
                return null;
            }

            return window.console;
        };


        /**
         *  Check if we should be writing to console. Console logging is only
         *  allowed when getEnv() returns 'test' or 'stage' as a failsafe to
         *  prevent leaking console statements to production.
         *
         *  @returns {Boolean} - if you can write to console
         */
        edapInternal.shouldConsole = function() {
            var appEnv = edapThis.getEnv(),
                console = edapInternal.getConsole();

            if (console === null) {
                return false;
            }

            if (appEnv === 'test' || appEnv === 'stage' || appEnv === 'dev') {
                return true;
            }

            return false;
        };


        /**
         *  Starts a console group() thread.
         *
         *  @param {String} [str] - String to write to console
         */
        edapInternal.consoleStartLog = function(str) {
            var console;

            if (!edapInternal.shouldConsole()) {
                return;
            }

            console = edapInternal.getConsole();

            if (typeof console.groupCollapsed === 'function') {
                console.groupCollapsed(str);
            } else if (typeof console.group === 'function') {
                console.group(str);
            } else {
                console.log(str);
            }
        };


        /**
         *  Writes a log/logs to console
         *  Accepts a variable number of parameters that are passed through
         *  to console.log. ex.
         *      console.log(str1, obj1, obj2);
         *
         *  @returns {String} - rfc4122 version 4 compliant guid
         */
        edapInternal.consoleLog = function() {
            var console;

            if (!edapInternal.shouldConsole()) {
                return;
            }

            console = edapInternal.getConsole();
            console.log.apply(console, arguments);
        };


        /**
         *  Writes a log/logs to the end of a console group
         *  Accepts a variable number of parameters that are passed through
         *  to console.log. ex.
         *      console.log(str1, obj1, obj2);
         *
         *  @returns {String} - rfc4122 version 4 compliant guid
         */
        edapInternal.consoleEndLog = function() {
            var console;

            if (!edapInternal.shouldConsole()) {
                return;
            }

            console = edapInternal.getConsole();

            if (arguments.length > 0) {
                edapInternal.consoleLog.apply(edapInternal, arguments);
            }

            if (typeof console.groupEnd === 'function') {
                console.groupEnd();
            }
        };



        /**
         * 
         * Public Methods
         *
         */



        /**
         *  Assign a callback to an event.
         *  Callbacks can be either synchronous or asynchronous. See examples.
         *
         *  NOTE: If you create an async callback you MUST be sure that the callback calls
         *        the async() function in normal synchronous execution of the functon, and
         *        guarentee that done() is always called at some point. Not calling done()
         *        will block the Edap.trigger Doneable from resolving and running any associated
         *        callbacks.
         *
         *  @example
         *  // Non-async callback function assigned to the 'myEvent' event
         *  var uuid = Edap.on('myEvent', function(data) {
         *      // do something when 'myEvent' is fired. You also have access to all the data!
         *  });
         *
         *  @example
         *  // Async callback function assigned to the 'myEvent' event
         *  // NOTE: It you are creating a sync callback you MUST 
         *  var uuid = Edap.on('myEvent', function(data, async, done) {
         *      async();
         *      // Notice I assigned to .always() instead of .done() to guarentee done()
         *      // is called even if the GET request fails.
         *      $.get('/some/endpoint').always(done);
         *  });
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event, cannot be a uuid
         *  @param {Function} [cb] - The callback to register with the event. See examples for more details
         *  @param {Object} [opts] - Supported options:
         *                            - logName: overrides the existing logName to put into Edap history
         *  @returns {String} - rfc4122 version 4 compliant guid
         */
        this.on = function(eventName, cb, opts) {
            var logName,
                callbackData,
                err;

            opts = opts || {};
            logName = opts.logName || 'on';

            edapInternal.logHistory(['Called',logName+'()','for event',eventName,'with',JSON.stringify(opts)].join(' '));

            if (typeof eventName !== 'string') {
                err = new TypeError(logName + '(): eventName must be a string');
                edapInternal.error(err);
                throw err;
            }

            if (eventName.match(uuidRegex)) {
                err = new TypeError(logName + '(): eventName cannot be a uuid');
                edapInternal.error(err);
                throw err;
            }

            if (typeof cb !== 'function') {
                err = new TypeError(logName + '(): cb must be a function');
                edapInternal.error(err);
                throw err;
            }


            callbackData = {
                uuid: edapInternal.createUuid(),
                cb: cb
            };

            if (edapInternal.callbacks.hasOwnProperty(eventName)) {
                edapInternal.callbacks[eventName].push(callbackData);
            } else {
                edapInternal.callbacks[eventName] = [callbackData];
            }

            edapInternal.logHistory([logName +'():','Added event listener for',eventName].join(' '));
            this.trigger('edap.listener.added', {edapaddedeventname: eventName, edaplisteneruuid: callbackData.uuid});

            return callbackData.uuid;
        };



        /**
         *  Removes a callback. See examples.
         *  
         *  @example
         *  // Remove all the callbacks for a specific event
         *  Edap.off('myEvent');
         *
         *  @example
         *  Removes the uuid from being a callback
         *  Edap.off(uuid);
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventNameOrUuid] - event name or uuid to remove
         *  @returns {Edap} - Returns this Edap instance to support chaining
         */
        this.off = function(eventNameOrUuid) {
            var logName = 'off',
                cbs,
                i,
                len,
                err;

            if (typeof eventNameOrUuid === 'string') {

                // Passed in a UUID
                if (eventNameOrUuid.match(uuidRegex)) {

                    edapInternal.logHistory(['Called',logName+'()','with uuid',eventNameOrUuid].join(' '));

                    for(var event in edapInternal.callbacks) {
                        if (edapInternal.callbacks.hasOwnProperty(event)) {
                            for (i=0, len=edapInternal.callbacks[event].length; i<len; i++) {
                                if (edapInternal.callbacks[event][i] && edapInternal.callbacks[event][i].uuid === eventNameOrUuid) {
                                    edapInternal.callbacks[event].splice(i,1);
                                    edapInternal.logHistory([logName+'():','removed uuid',eventNameOrUuid,'from',event,'queue'].join(' '));
                                    this.trigger('edap.listener.removed',{edapremovedeventname: event, edaplisteneruuid: eventNameOrUuid});
                                }
                            }
                        }

                    }

                // Passed in an event name
                } else {
                    edapInternal.logHistory(['Called',logName+'()','with string',eventNameOrUuid].join(' '));

                    if (edapInternal.callbacks.hasOwnProperty(eventNameOrUuid)) {
                        cbs = edapInternal.callbacks[eventNameOrUuid];

                        edapInternal.callbacks[eventNameOrUuid] = [];

                        // Make sure we trigger a "removedListener" event from
                        for(i=0,len=cbs.length; i<len; i++) {
                            this.trigger('edap.listener.removed',{edapremovedeventname: eventNameOrUuid, edaplisteneruuid: cbs[i].uuid});
                        }

                        edapInternal.logHistory([logName+'():','cleared all cbs for',eventNameOrUuid,'event'].join(' '));
                    }
                }

            } else {
                edapInternal.logHistory(['Called',logName+'()', 'invalid'].join(' '));

                err = new TypeError(logName + '(): must be passed an event name string or a function');
                edapInternal.error(err);
                throw err;
            }

            return this;
        };


        /**
         *  Sets a callback for an event that will only fire once. Supports all types of callbacks
         *  supported by on();
         *  @see this.on
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event
         *  @param {Function} [cb] - The callback to register with the event. See on() examples for more details
         *  @param {Object} [opts] - Supported options: see on() options
         *  @returns {String} - rfc4122 version 4 compliant guid
         */
        this.once = function(eventName, cb, opts) {
            var that = this,
                onUuid;

            function runOnce() {
                cb.apply(that,arguments);
                that.off(onUuid);
            }

            opts = opts || {};
            opts.logName = 'once';

            onUuid = this.on(eventName, runOnce, opts);

            return onUuid;
        };


        /**
         *  Trigger an event and fire all the associated callbacks
         *
         *  @example
         *  // Firing an event with only the Data Layer being passed
         *  Edap.trigger('myEvent');
         *
         *  @example
         *  // Firing an event with event-specific data only the Data Layer being passed
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2});
         *
         *  @example
         *  // Firing an event that should be queued for processing later
         *  // This mechanism should be used when firing an event near a page transition
         *  // so the next page can process the event, increasing the odds that the event
         *  // is processed fully.
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2}, {delayProcessing: true});
         *
         *  @example
         *  // Sets a maxWaitTime of 4 seconds to finish processing all event listeners
         *  // related to the 'myEvent' event. Overwrites the default maxWaitTime in
         *  // edap.internal.config.maxWaitTime.
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2}, {maxWaitTime: 4});
         *
         *  @example
         *  // Firing an event and assigning a callback when all the event processing
         *  // has completed
         *  Edap.trigger('myEvent', {mydata1: 1, mydata2: 2}).then(function() {
         *      // do something now that all the event callbacks are done
         *  });
         *
         *  @method
         *  @throws {TypeError} - Parameters do not pass validation
         *  @param {String} [eventName] - Name of the event, cannot trigger wildcard event explicitly
         *  @param {Function} [cb] - The callback to register with the event. See examples for more details
         *  @param {Object} [opts] - Supported options:
         *                            - delayProcessing: queue this event and data up to be processed on the next 'edap.flush' event
         *                            - maxWaitTime: time in seconds to wait for callbacks to complete before calling the 
         *                                           trigger complete callback. Overwrites the Edap default maxWaitTime of 2 seconds.
         *  @returns {Doneable} - Resolves when all the callbacks for the event are done
         */
        this.trigger = function(eventName, data, opts) {
            var that = this,
                logName = 'trigger',
                waitTime = edapInternal.config.maxWaitTime,
                err;

            opts = opts || {};

            edapInternal.logHistory(['Called',logName+'()','for event',eventName,'with data',JSON.stringify(data),'with opts',JSON.stringify(opts)].join(' '));

            if (typeof eventName !== 'string') {
                err = new TypeError(logName + '(): eventName must be a string');
                edapInternal.error(err);
                throw err;
            }

            if (eventName.match(/[*]$/)) {
                err = new TypeError(logName + '(): cannot trigger wildcard event explicitly ' + eventName);
                edapInternal.error(err);
                throw err;
            }

            return new Doneable(function(done) {
                var cbs = edapInternal.getCallbacks(eventName),
                    shouldConsoleLog = !edapInternal.alreadyExists(eventName, ignoredConsoleMessages),
                    completeCbs = 0,
                    numAsync = 0,
                    isAllDone = false,
                    maxWaitTimeout,
                    numCbs,
                    clonedData,
                    i;

                function doDone() {
                    clearTimeout(maxWaitTimeout);
                    isAllDone = true;

                    if (cbs.length) {
                        edapInternal.logHistory(['Executed',cbs.length,'cbs total with',numAsync,'async for event',eventName].join(' '));
                    }

                    done();
                }

                function checkDone() {
                    if (isAllDone) {
                        return;
                    }

                    completeCbs++;

                    if (completeCbs === numCbs) {
                        doDone();
                    }
                }

                // Function to encapsulate all the logic of executing a callback
                function callCallback(cb) {
                    var isAsync = false,
                        isDone = false,
                        retVal = 0;

                    function setAsync() {
                        isAsync = true;
                    }

                    function callbackDone() {
                        if (!isDone) {
                            isDone = true;
                            checkDone();
                        }
                    }

                    try {
                        cb.cb.call(that, clonedData, setAsync, callbackDone);

                        if (!isAsync) {
                            callbackDone();
                        } else {
                            retVal = 1;
                        }
                    } catch(e) {
                        var err = new Error(logName + '(): error caught in processing event: '+eventName+' stacktrace:'+e.stack);
                        edapInternal.error(err);
                        callbackDone();
                    }

                    return retVal;
                }

                function buildData(optData) {
                    optData.edapeventname = eventName;
                    return edapInternal.extend(edapThis.getDL(), optData);
                }

                if (shouldConsoleLog) {
                    edapInternal.consoleStartLog('EDAP event triggered: ' + eventName);
                    edapInternal.consoleLog('Passed Data: ', data);
                    edapInternal.consoleLog('Options: ', opts);
                }

                if (data === undefined || data === null) {
                    data = {};
                }

                if (opts.delayProcessing === true) {
                    // Get our cloned data set
                    clonedData = buildData(data);

                    if (shouldConsoleLog) {
                        edapInternal.consoleEndLog('Shrinkwrapped Data: ', clonedData);
                    }

                    edapInternal.storeCmd(eventName, clonedData);

                    edapInternal.logHistory(['Delayed processing for event',eventName].join(' '));

                    doDone();
                    return;
                }

                if (typeof opts.maxWaitTime === 'number') {

                    // Overwrite the default on a per-trigger basis
                    waitTime = opts.maxWaitTime;
                }

                // waitTime must be >0 to assign the Timeout
                if (waitTime > 0) {
                    maxWaitTimeout = setTimeout(function() {
                        edapInternal.logHistory([logName+'()',waitTime+'sec','timeout reached for event',eventName].join(' '));
                        doDone();
                    }, waitTime * 1000);
                }

                // If there are no callbacks then just return a done Doneable
                if (cbs.length === 0) {
                    if (shouldConsoleLog) {
                        edapInternal.consoleEndLog('Shrinkwrapped Data: skipped (no callbacks registered)');
                    }
                    doDone();
                    return;
                }

                // Get our cloned data set
                clonedData = buildData(data);

                if (shouldConsoleLog) {
                    edapInternal.consoleEndLog('Shrinkwrapped Data: ', clonedData);
                }

                for(i=0, numCbs=cbs.length; i<numCbs; i++) {
                    numAsync += callCallback(cbs[i]);
                }

            }, {error: this.error});
        };


        /**
         *  Public interface for debug logger
         *  
         *  @method
         *  @see this.internal.log
         */
        this.log = edapInternal.log;

        /**
         *  Public interface for error logger
         *  
         *  @method
         *  @see this.internal.error
         */
        this.error = edapInternal.error;




        /**
         *  Used to mimic the async version of Edap.
         *
         *  @example
         *  // Registering a callback for an event
         *  Edap.push(['on','thatCoolEvent', function(data) {
         *      //do something cool!
         *  }]);
         *
         *  @example
         *  // Snippet of how to queue events before Edap has loaded (using global)
         *  window.edap = window.edap || [];
         *  window.edap.push(['on','thatCoolEvent', function(data) {
         *      //do something cool!
         *  }]);
         *
         *  @example
         *  // Snippet that shows how to use the generic function format
         *  window.edap = window.edap || [];
         *  window.edap.push([function(edap) {
         *      edap.on('thatCoolEvent', function(data) {
         *          //do something cool!
         *      });
         *  }]);
         *
         *  @method
         *  @param {Array} [data] - Can be in either a specific command format or generic
         *                          function format.
         *                          // specific command format
         *                          ["nameofmethod",methodarg1,methodarg2,...]
         *                          
         *                          // generic function format
         *                          [function]
         *                          When using this format the function is passed the edap
         *                          instance as the function parameter
         *
         */
        this.push = function(data) {
            var logName = 'push',
                ret,
                cmd,
                err;

            edapInternal.logHistory(['Called',logName+'()','with data',JSON.stringify(data)].join(' '));

            if (typeof data === 'function') {
                    try {
                        data.call(this, this);
                    } catch(e) {
                        var err = new Error(logName + '(): error caught in processing queue item: '+JSON.stringify(data)+' stacktrace:'+e.stack);
                        edapInternal.error(err);
                    }
            } else if (data instanceof Array) {
                cmd = data.shift();
                if (this.hasOwnProperty(cmd)) {
                    ret = this[cmd].apply(this,data);

                    // ex. push(['trigger','test.event',{},{},myTriggerCompleteFunc]);
                    if (cmd === 'trigger' && typeof data[3] === 'function') {
                        ret.then(data[3]);
                    }
                }
            } else {
                err = new TypeError(logName + '(): data must be a Function or an Array');
                edapInternal.error(err);
                throw err;
            }
        };


        /**
         *  Get a list of all the active scopes
         *
         *  @method
         *  @returns [Object] - Object where the keys are the scope names and values are the instances
         */
        this.getScopeInfo = function() {
            var logName = 'getScopeList';

            edapInternal.logHistory(['Called',logName+'()'].join(' '));
 
            return edapInternal.scopes;
        };


        /**
         *  Creates a scope (or context) within Edap that provides 2 things:
         *      1. Guaranteed unique context names. This can be used to prevent modules from registering
         *         their event listeners multiple times.
         *      2. Provides Scope.prototype.unregister() that will unregister any listeners that were 
         *         set through the Scope. The purpose would be to limit the amount of application logic
         *         needed to cleanup any module-specific listeners when the module is destoyed.
         *      3. Provides Scope.prototype.destroy() to remove a scope
         *      4. Provides Scope.prototype.getScopeData() as a scope-specific data store
         *
         *  This proxies all the Edap methods and features so it has access to all the same methods. 
         *
         *  @example
         *  // simple example of all the exposed methods of a Scope
         *  var myScope = new Edap.Scope('myScopeName');
         *  var onUuid = myScope.on('test', function(data){...});
         *  myScope.once('test', function(data){...});
         *  myScope.trigger('myEvent',{testData: true});
         *  myScope.off(onUuid);
         *  myScope.log('myScopeName just logged a msg');
         *  myScope.error('myScopeName just logged a error');
         *  myScope.getScopeData().myDataEl = 1;
         *  myScope.unregister();
         *  myScope.destroy();
         *
         *  @constructor
         *  @throws {TypeError} - when not called with "new" or invalid name argument
         *  @returns {Edap.Scope} - this Edap.Scope instance
         */
        this.Scope = function(name) {
            var err;

            this.name = name;
            this.uuids = {};

            if (this === edapThis) {
                err = new TypeError('Edap.Scope must be called with the "new" keyword');
                edapInternal.error(err);
                throw err;
            }

            if (typeof name !== 'string' || name.length === 0) {
                err = new TypeError('name must be a non-zero length string');
                edapInternal.error(err);
                throw err;
            }

            if (edapInternal.scopes.hasOwnProperty(name)) {
                err = new TypeError(name + ' scope already registered');
                edapInternal.error(err);
                throw err;
            }

            edapInternal.scopes[name] = {
                instance: this,
                data: {}
            };

            for(var propName in edapThis) {
                if (edapThis.hasOwnProperty(propName)) {
                    this[propName] = edapThis[propName];
                }
            }

            this.on = function(eventName) {
                var uuid = edapThis.on.apply(edapThis, arguments);
                this.uuids[uuid] = true;

                edapInternal.logHistory(['Scope',name,'registered new callback for event', eventName, 'uuid', uuid].join(' '));

                return uuid;
            };

            this.off = function(uuid) {
                var ret = edapThis.off.apply(edapThis, arguments);

                if (this.uuids[uuid]) {
                    this.uuids[uuid] = undefined;
                    edapInternal.logHistory(['Scope',name,'removed callback uuid', uuid].join(' '));
                }

                return ret;
            };

            this.unregister = function() {
                edapInternal.logHistory(['Called unregister() for Scope',this.name].join(' '));

                for(var uuid in this.uuids) {
                    if (this.uuids.hasOwnProperty(uuid) && this.uuids[uuid]) {
                        edapThis.off(uuid);
                        this.uuids[uuid] = undefined;
                    }
                }
            };

            this.destroy = function() {
                var oldName = this.name,
                    deadFunc = function() {
                        throw new Error('Attempting to access destroyed edap scope ' + oldName);
                    };

                edapInternal.logHistory(['Called destroy() for Scope',this.name].join(' '));

                for(var prop in this) {
                    if (this.hasOwnProperty(prop)) {
                        this[prop] = deadFunc;
                    }
                }

                edapInternal.scopes[name] = undefined;
            };
    
            this.getScopeData = function() {
                var dataObj = edapInternal.scopes[name].data,
                    err;

                if (dataObj !== null && typeof dataObj === 'object') {
                    return dataObj;
                } else {
                    err = new TypeError(name + ' scope already registered');
                    edapInternal.error(err);
                    throw err;
                }
            };

            return this;
        };


        /**
         *  Abstraction around getting the global Data Layer
         *
         *  @returns {Object} - Returns the ADL.
         */
        this.getDL = function() {
            return window[edapThis.globalOptions.dataLayerName] || {};
        };


        /**
         *  Getter for the current environment (production/stage/test)
         *
         *  @param {Object} [optAdl] - Optional ADL object to use instead of the page ADL
         *  @returns {String} - (optAdl or DL).appenvironment value. Defaults to 'unknown'
         */
        this.getEnv = function(optAdl) {
            var dl;

            if (optAdl && typeof optAdl === 'object') {
                dl = optAdl;
            } else {
                dl = edapThis.getDL();
            }

            return dl.appenvironment || 'unknown';
        };


        // Constructor code

        // Make sure the initial data structure is setup
        edapInternal.reset();

        this.on('edap.reset', function() {
            edapInternal.reset();
            edapInternal.setState('ready');
        });

        // Listen to the flush event to flush the persistently stored cmds
        this.on('edap.flush', function(data, async, done) {

            async();

            if (edapInternal.savedCmds instanceof Array) {
                edapInternal.flushAsyncCommands(edapInternal.savedCmds);
                edapInternal.savedCmds = null; // Clean up this memory
            }

            edapInternal.processStoredCmds().then(done);
        });

        // Save any aysnc-queued commands before we squash it
        if (edapInternal.getGlobal() instanceof Array) {
            edapInternal.savedCmds = edapInternal.getGlobal();
        }

        edapInternal.setState('ready');

        return this;
    }

    return new Edap(edapOpts);
});

